import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/api";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
    const [admin, setAdmin] = useState(null); // null | false | {email, user_id, password_must_change}

    const refresh = useCallback(async () => {
        try {
            const { data } = await adminApi.get("/admin/auth/me");
            setAdmin(data.admin);
        } catch {
            setAdmin(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = useCallback(async (email, password) => {
        const { data } = await adminApi.post("/admin/auth/login", { email, password });
        setAdmin(data.admin);
        return data;
    }, []);

    const changePassword = useCallback(async (currentPassword, newPassword) => {
        await adminApi.post("/admin/auth/change-password", {
            current_password: currentPassword,
            new_password: newPassword,
        });
        // Refresh me to clear password_must_change flag in client state.
        await refresh();
    }, [refresh]);

    const logout = useCallback(async () => {
        try {
            await adminApi.post("/admin/auth/logout");
        } finally {
            setAdmin(false);
        }
    }, []);

    return (
        <AdminAuthContext.Provider value={{ admin, refresh, login, logout, changePassword }}>
            {children}
        </AdminAuthContext.Provider>
    );
}

export const useAdminAuth = () => {
    const ctx = useContext(AdminAuthContext);
    if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
    return ctx;
};
